import { createApp } from 'vue'
import App from './App.vue'
import { router } from '@/services/router'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import { aliases, mdi } from 'vuetify/iconsets/mdi'
import { Tell } from '@/services/Tell'
import { AuthService } from '@/contracts/AuthService'
import { EntraAuthService } from '@/services/EntraAuthService'
import { CommercialApi } from '@/services/CommercialApi'
import { EntraAuthConfig as entraAuthConfig } from '@/services/EntraAuthConfig'
import { CommercialApiConfig as commercialApiConfig } from '@/services/CommercialApiConfig'
import { SignalRMessageService } from '@/services/SignalRMessageService'
import telemetryService from '@/services/TelemetryService'
import { TelemetryContract } from '@/contracts/TelemetryContract';
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import './assets/styles/main.scss'
import 'vuetify/styles'
import "@mdi/font/css/materialdesignicons.css";
import VueGoogleMaps from "@fawmi/vue-google-maps";
import { GoogleMap } from 'vue3-google-map';
import { useAppStore } from '@/stores/app.store';
import colors from 'vuetify/util/colors'
import { AuthenticatedFetch, ResilientAuthenticatedFetch } from './services/AuthenticatedFetch'
import { VTreeview } from 'vuetify/labs/VTreeview'
import { TinyEmitterEventBus } from './services/TinyEmitterEventBus'
import { EventBus } from '@/contracts/EventBus'
import { PolicyService } from '@/contracts/PolicyService'
import { RateService } from '@/contracts/RateService'
import { ConfigService } from '@/contracts/ConfigService'
import { MessageService } from '@/contracts/MessageService'

const lightColors = {
	'button-default': '#333333',
	'on-button-default': '#E0E0E0',

	primary: '#245e36',
	'on-primary': '#ffffff',
	'primary-container': '#e0f2e9',
	'on-primary-container': '#0d3a1f',

	secondary: '#d67c3b',
	'on-secondary': '#ffffff',
	'secondary-container': '#fbe8dc',
	'on-secondary-container': '#4a2c1a',

	tertiary: '#6099ac',
	'on-tertiary': '#ffffff',
	'tertiary-container': '#dbeffa',
	'on-tertiary-container': '#105163',

	surface: '#FFFFFF',
	'on-surface': '#1C1B1F',
	'surface-variant': '#E7E0EC',
	'on-surface-variant': '#49454F',
	outline: '#CAC4D0',

	error: '#c94241',
	info: '#6099ac',
	success: '#4d7e5a',
	warning: '#d67c3b',
}

const darkColors = {
	// surfaces and backgrounds in dark mode need to be set
	background: '#121212', // app background
	'on-background': '#E0E0E0', // body text
	surface: '#1E1E1E', // cards, sheets
	'on-surface': '#E0E0E0', // headings, cards
	'surface-variant': '#2C2C2C', // e.g. list-group headers
	'on-surface-variant': '#E0E0E0', // secondary text
	
	'button-default': '#333333',
	'on-button-default': '#E0E0E0',

	outline: '#5F5F5F', // dividers, outlines

	primary: '#8FD49E', // M3 tone‑80 of #245E36 – readable on dark surfaces
	'on-primary': '#003919',
	'primary-container': '#3C3C3C', // e.g. app bars, toolbars
	'on-primary-container': '#E0E0E0', // text on primary containers

	secondary: '#FFB77C', // M3 tone‑80 of #D67C3B
	'on-secondary': '#4E2600',
	'secondary-container': '#2C2C2C', // e.g. secondary buttons
	'on-secondary-container': '#E0E0E0', // text on secondary containers
	tertiary: '#A0D4E8', // M3 tone‑80 of #6099AC
	'on-tertiary': '#003544',
	'tertiary-container': '#1C1C1C', // e.g. tertiary buttons
	'on-tertiary-container': '#E0E0E0', // text on tertiary containers

	error: '#CF6679',
	'on-error': '#3E141C',
	warning: '#EFB700',
	'on-warning': '#3B3300',
	info: '#A0D4E8', // match tertiary‑family light tone
	'on-info': '#003544',
	success: '#7ABF8A', // M3 tone‑80 of #4D7E5A
	'on-success': '#003919',
}

export function startApp(authService: AuthService,
	policyService: PolicyService,
	rateService: RateService,
	configService: ConfigService,
	eventBus: EventBus
) {
	//const data = await mockService.getData();
	const app = createApp(App);

	const pinia = createPinia();

	pinia.use(piniaPluginPersistedstate);

	app.use(router);
	app.use(pinia);

	// Detect system preference for dark mode
	const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
	let defaultTheme = prefersDarkMode ? 'dark' : 'light';

	// Check the app store for current settings
	const appStore = useAppStore();

	// if the app store has settings; use those
	if (appStore.getTheme != null) {
		if (appStore.getTheme != 'system') {
			defaultTheme = appStore.getTheme;
		}
	}
	else {
		// save this to the store
		if (prefersDarkMode) {
			appStore.setTheme('system');
		}
		else {
			appStore.setTheme(defaultTheme);
		}
	}

	const vuetify = createVuetify({
		components,
		directives,
		icons: {
			defaultSet: 'mdi',
			aliases,
			sets: {
				mdi,
			},
		},
		theme: {
			defaultTheme: defaultTheme,
			themes: {
				light: {
					dark: false,
					colors: lightColors
				},
				dark: {
					dark: true,
					colors: darkColors
				},
			},
		},
		defaults: {
			MBtn: {
				background: 'button-default',
				color: 'on-button-default'
			}
		}
	});

	app.use(vuetify);

	app.use(VueGoogleMaps, {
		load: {
			key: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
			libraries: ['places'],
		}
	});

	app.component('GoogleMap', GoogleMap);
	app.component('VTreeview', VTreeview);

	app.provide('auth', authService);
	app.provide('telemetry', telemetryService);
	app.provide('tell', new Tell(eventBus));
	app.provide('policyService', policyService);
	app.provide('rateService', rateService);
	app.provide('configService', configService);

	app.provide('eventBus', eventBus);

	app.mount('#app');

}

if (process.env.VUE_APP_IS_TESTING !== 'true') {
	//start regularly
	const authService = new EntraAuthService(entraAuthConfig);
	const commercialApi = new CommercialApi(commercialApiConfig, new ResilientAuthenticatedFetch(new AuthenticatedFetch(authService)));
	const eventBus = new TinyEmitterEventBus();

	startApp(authService,
		commercialApi,
		commercialApi,
		commercialApi,
		eventBus
	);
}
// startApp(new EntraAuthService(entraAuthConfig));